import {PILL} from '../../templates/components/Parts/Medication/administration';
import app from '../../app'
import {ROLE_DSS_ALGORITHM_ACCESS} from "../user/role-tree";
import {simpleClone} from "../../../helper/objects";
import {uniqueValues} from "../../../helper/arrays";
import { newMedicationSchedule } from './state';

export const DSSMedicationSchedules = (state) => state
    .DSSMedicationSchedules || [];

export const DSSMedicationSchedule = (state, getters) => getters
    .DSSMedicationSchedules[0] || {};

export const medicationSchedules = (state, getters) => getters
    .DSSMedicationSchedule.medicationSchedules || [];

export const cardioMedicationSchedules = (state, getters) => getters
    .medicationSchedules.filter((item) => item.medication && item.medication.medicationType && item.medication.medicationType === 'cardio');

export const otherMedicationSchedules = (state, getters) => getters
    .medicationSchedules.filter((item) => item.medication && item.medication.medicationType && item.medication.medicationType === 'non-cardio');

export const algorithm = (state, getters) => getters
    .DSSMedicationSchedule.algorithm || {};

export const debugDSSAlgorithm = () => app.hasDSSDebugCookie && app.store.getters['user/isGranted'](ROLE_DSS_ALGORITHM_ACCESS)

export const unhandledMedicationRecommendations = (state, getters) => getters
    .algorithm.unhandledMedicationRecommendations || [];

export const mapRecommendationsToClasses = (state, getters) => (recommendations) => {
    const medicationClasses = [];
    recommendations.forEach((recommendation) => {
        recommendation.medicationClassRecommendations.forEach((medicationClassRecommendation) => {
            const medicationClassAlreadyExists = medicationClasses.some((medicationClass) => medicationClass.key === medicationClassRecommendation.medicationClass.key);
            if (!medicationClassAlreadyExists) {
                const medicationClassRecommendationCopy = simpleClone(medicationClassRecommendation);
                // Also add Min/Max dosages and values
                const medicationClass = {
                    maxDosageUnit: medicationClassRecommendationCopy.maxDosageUnit,
                    minDosageUnit: medicationClassRecommendationCopy.minDosageUnit,
                    maxDosageValue: medicationClassRecommendationCopy.maxDosageValue,
                    minDosageValue: medicationClassRecommendationCopy.minDosageValue,
                    ...medicationClassRecommendationCopy.medicationClass,
                };
                medicationClasses.push(medicationClass);
            }
        });
    });

    return medicationClasses;
}

export const getDefaultNameFromRecommendations = (state, getters) => (recommendations) => {
    const names = [];

    recommendations.forEach((recommendation) => {
        recommendation.medicationClassRecommendations.forEach((medicationClassRecommendation) => {
          if (medicationClassRecommendation.name){
              names.push(medicationClassRecommendation.name);
          }
        });
    });

    return uniqueValues(names).join(' / ');
}

/*
    Medication classes do not contain complete schedules, instead of a frequency string (currently only BIWEEKLY/DAILY exist)
    Unhandled medications (yellow rows) need to base their default frequency based on this frequency string. If the
    unhandled medication recommendation has a medication which has BIWEEKLY, return a biweekly schedule, otherwise return
    default daily schedule.
*/
export const getIntervalsFromUnhandledRecommendations = (state, getters) => (recommendations) => {
    for (let i=0; i<recommendations.length; i++){
        const recommendation = recommendations[i];

        if (recommendation.medicationClassRecommendations){
            for (let j=0; j<recommendation.medicationClassRecommendations.length; j++){
                const classRecommendation = recommendation.medicationClassRecommendations[j];
                const frequency = classRecommendation?.medicationClass?.frequency;

                if (frequency === 'BIWEEKLY'){
                    return [{interval: 'P2W', intervalOn: true}];
                }
            }
        }
    }

    return [{interval: 'P1D', intervalOn: true}];
}

export const mapRecommendationsToGuidelines = (state, getters) => (recommendations) => {
    const guidelines = [];
    recommendations.forEach((recommendation) => {
        recommendation.guidelines.forEach((checkGuideline) => {
            const guidelineAlreadyExists = guidelines.some((guideline) => guideline.key === checkGuideline.key);
            if (!guidelineAlreadyExists) {
                guidelines.push(checkGuideline);
            }
        });
    });

    return guidelines;
}


export const cardioMedicationSchedulesWithRecommendationInfo = (state, getters) => {
    return getters.cardioMedicationSchedules.map((medicationSchedule) => {
        return getters.makeMedicationScheduleWithRecommendationInfo(medicationSchedule.medicationRecommendations, medicationSchedule);
    });
}

export const otherMedicationSchedulesWithRecommendationInfo = (state, getters) => {
    return getters.otherMedicationSchedules.map((medicationSchedule) => {
        return getters.makeMedicationScheduleWithRecommendationInfo(medicationSchedule.medicationRecommendations, medicationSchedule);
    });
}

/**
 * Format recommendations to a Medication Schedule
 * @function vuex-DSS.makeMedicationScheduleWithRecommendationInfo
 * @param state
 * @param getters
 * @returns {function(*=, *=): {item: *}}
 */
export const makeMedicationScheduleWithRecommendationInfo = (state, getters) => (medicationRecommendations, medicationSchedule = null) => {
    if (!medicationRecommendations){
        medicationRecommendations = [];
    }

    const recommendedClasses = getters.mapRecommendationsToClasses(medicationRecommendations);
    let classes = [];
    const isUnhandled = medicationSchedule === null;

    if (isUnhandled){

        // If it's a yellow row (unhandled) and only 1 medication class is recommended, automatically fill it in
        classes = recommendedClasses.length <= 1 ? recommendedClasses : [];

        // For now we use the first administration
        // TODO: What with multiple, what is the default?
        const administration = recommendedClasses?.[0].administration?.[0];

        // Setup default frontend MedicationSchedule
        medicationSchedule = {
            id: '',
            name: getters.getDefaultNameFromRecommendations(medicationRecommendations),
            type: 'cardio',
            administration: administration ? administration : PILL,
            dosages: [],
            intakeMoments: [],
            schedule: {
                intervals: getters.getIntervalsFromUnhandledRecommendations(medicationRecommendations),
                monthsOfYear: [],
                daysOfMonth: [],
                daysOfWeek: [],
                weeksOfMonth: [],
                weeksOfYear: [],
                times: [],
                anchorDate: '',
                note: '',
            },
            classes: classes.map(medicationClass => medicationClass.key),
            morningAmount: 0,
            noonAmount: 0,
            afternoonAmount: 0,
            eveningAmount: 0,
            notes: app.translationManager.t('dss.recommendedByAlgorithm'),
            combinedDrugs: [],
            // guidelines: recommendation.guidelines || [],
            // medicationRecommendationId: recommendation.medicationRecommendationId ? recommendation.medicationRecommendationId : ''
        }
    }else{
        // Mapping backend MedicationSchedule to frontend MedicationSchedule (see above)

        medicationSchedule = simpleClone(medicationSchedule);
        medicationSchedule.classes = medicationSchedule.medication.classes.map(medicationClass => medicationClass.key);
        medicationSchedule.administration = medicationSchedule?.medication?.form?.coding?.[0]?.code;

        if (!medicationSchedule.administration){
            medicationSchedule.administration = PILL;
        }

        medicationSchedule.name = medicationSchedule.medication.name;

        if(medicationSchedule.schedule != null) {
            delete medicationSchedule.schedule?.id;
            delete medicationSchedule.schedule?.__typename;

            if (medicationSchedule.schedule?.intervals !== null){
                medicationSchedule.schedule?.intervals.forEach(interval => {
                    delete interval.__typename;
                })
            }
        }

        medicationSchedule.dosages = medicationSchedule.dosage.doseAndRate.map((doseAndRate) => {
            return {
                unit : doseAndRate.doseQuantity.code,
                value : doseAndRate.doseQuantity.value,
                id : doseAndRate.id
            }
        })

        if (!medicationSchedule.notes) {
            medicationSchedule.notes = '';
        }
    }

    return {
        isUnhandled : isUnhandled,
        recommendedClasses : recommendedClasses,
        medicationRecommendations: medicationRecommendations,
        guidelines : getters.mapRecommendationsToGuidelines(medicationRecommendations),
        medicationSchedule : medicationSchedule
    }
}

export const unhandledMedicationSchedulesWithRecommendationInfo = (state, getters) => {
    return getters.unhandledMedicationRecommendations.map((recommendation) => {
        return getters.makeMedicationScheduleWithRecommendationInfo([recommendation]);
    });
};

export const medicationClasses = (state) => state.medicationClasses;

export const patientQuestionnaires = (state) => state.patientQuestionnaires || [];

export const lastQuestionnaire = (state, getters) => {
    if (getters.patientQuestionnaires.length) {
        return getters.patientQuestionnaires[0];
    }

    return {};
}

export const lastQuestionnaireEntries = (state, getters) => {
    const {entries} = getters.lastQuestionnaire;

    if (entries && entries.length) {
        return entries;
    }

    return [];
}

export const auditTrails = (state) => state.auditTrails || {edges: []};

export const auditTrailsEdges = (state, getters) => getters
    .auditTrails.edges || [];

export const auditTrailsNodes = (state, getters) => getters
    .auditTrailsEdges.map((item) => item.node);

export const newMedication = () => newMedicationSchedule();

export const prescriptionRecentlyUpdatedWarning = (state, getters) => {
    return state.prescriptionRecentlyUpdatedWarning;
}

export const schedulesToBeDeleted = (state) => {
    return state.schedulesToBeDeleted;
}

export const DSSFirstLoad = (state) => {
    return state.DSSFirstLoad;
}

export const DSSLastAuditTrails = (state) => {
    return state.DSSLastAuditTrails;
}